import { createContext, useContext, useCallback, useRef, useEffect, type ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";

interface NavigationHistoryContextValue {
  /** Go back using the custom stack (or fallback) */
  goBack: (fallback?: string) => void;
  /** Whether the custom stack has entries */
  canGoBack: () => boolean;
}

const NavigationHistoryContext = createContext<NavigationHistoryContextValue | null>(null);

export function NavigationHistoryProvider({ children }: { children: ReactNode }) {
  const stackRef = useRef<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const prevPathRef = useRef<string | null>(null);
  const isGoingBackRef = useRef(false);

  // Track every location change and push the previous path onto the stack
  useEffect(() => {
    const current = location.pathname;
    const prev = prevPathRef.current;

    if (prev && prev !== current && !isGoingBackRef.current) {
      const stack = stackRef.current;
      // Don't push duplicate at top
      if (stack.length === 0 || stack[stack.length - 1] !== prev) {
        // If destination already in stack, truncate to prevent loops
        const existingIndex = stack.indexOf(current);
        if (existingIndex !== -1) {
          stackRef.current = stack.slice(0, existingIndex);
        } else {
          stackRef.current.push(prev);
        }
      }
    }

    isGoingBackRef.current = false;
    prevPathRef.current = current;
  }, [location.pathname]);

  const goBack = useCallback(
    (fallback = "/annonser") => {
      const stack = stackRef.current;
      isGoingBackRef.current = true;
      if (stack.length > 0) {
        const prev = stack.pop()!;
        navigate(prev);
      } else {
        navigate(fallback);
      }
    },
    [navigate],
  );

  const canGoBack = useCallback(() => stackRef.current.length > 0, []);

  return (
    <NavigationHistoryContext.Provider value={{ goBack, canGoBack }}>
      {children}
    </NavigationHistoryContext.Provider>
  );
}

export function useNavHistory() {
  const ctx = useContext(NavigationHistoryContext);
  if (!ctx) throw new Error("useNavHistory must be used within NavigationHistoryProvider");
  return ctx;
}
